import { useEffect, useMemo, useState } from "react";
import t, { AsHtml } from "utils/translate";

import type { OfferPlan } from "types/offerPlan";
import type { UseBundleReturn } from "types/offerBundle";
import type { ResultsUni5Cookie } from "types/cookie";

import getAidFromCookie from "utils/getAidFromCookie";
import {
  CORE_PRODUCT,
  DIABETES_PROGRAM,
  DIABETES_WORKSHOP_PROGRAM,
  GLP1_PROGRAM,
  GLP1_WORKSHOP_PROGRAM,
  PREMIUM_PRODUCT
} from "utils/resultFunctions/constants";
import getRswUrl from "utils/getRswUrl";
import { useBundle } from "utils/useBundle";
import daProps from "utils/daPropsPricingBtn";
import { loadResultsCookieByBaseUrl } from "components/Results/storeResultsData";

import useAppContext from "components/AppContext";
import ContentGutter from "components/ContentGutter";
import SiteWrapper from "components/SiteWrapper";
import Checklist from "components/Checklist";
import PricingCarousel from "components/Results/PricingCarousel";
import PricingCard from "components/Results/PricingCard";
import useUrlSlugContext from "components/UrlSlugContext";
import Loading from "components/Loading";
import Error from "components/ClinicCheckout/Error";
import OfferDisclaimer from "components/Results/OfferDisclaimer";
import { pageView } from "utils/analytics";
import getBundleByDate from "utils/getBundleByDate";
import { CORE, WORKSHOPS } from "components/Results/constants";

import styles from "./styles.module.scss";

export const ResultsUni5Pricing: React.FC = () => {
  const { newQuiz } = useAppContext();
  const { baseUrl, urlSlug } = useUrlSlugContext();
  const cookie = loadResultsCookieByBaseUrl(
    baseUrl
  ) as ResultsUni5Cookie | null;
  const resultsScreen = cookie?.resultsScreen || "";
  const isWorkshops = [
    PREMIUM_PRODUCT,
    DIABETES_WORKSHOP_PROGRAM,
    GLP1_WORKSHOP_PROGRAM
  ].includes(resultsScreen);

  const bundleId = getBundleByDate(isWorkshops ? WORKSHOPS : CORE);
  const { bundle, plans, isLoading, isError, retry }: UseBundleReturn =
    useBundle({
      bundleId:
        bundleId || t(`UNI_${isWorkshops ? WORKSHOPS : CORE}_OFFER_BUNDLE`)
    });
  const [selectedPlan, setSelectedPlan] = useState<OfferPlan>();

  useEffect(() => {
    if (bundle && plans.length) {
      let plan = plans.find(p => p.id === bundle.preSelectedOfferPlanId);

      if (!plan) {
        plan = plans[0];
      }

      setSelectedPlan(plan);
    }
  }, [bundle, plans]);

  useEffect(() => {
    pageView("pricing");
  }, []);

  const onSelection = index => {
    setSelectedPlan(plans[index]);
  };

  const checkoutUrl = useMemo(() => {
    const assessmentId = getAidFromCookie(newQuiz, baseUrl);

    return getRswUrl(
      selectedPlan?.id,
      bundle?.id,
      assessmentId,
      urlSlug,
      isWorkshops ? PREMIUM_PRODUCT : CORE_PRODUCT,
      false
    );
  }, [selectedPlan?.id, bundle?.id, resultsScreen, isWorkshops]);

  const checklistItems = {
    [CORE_PRODUCT]: [
      t("POINTS_CHECKLIST_ITEM1"),
      t("POINTS_CHECKLIST_ITEM2"),
      t("POINTS_CHECKLIST_ITEM3"),
      t("POINTS_CHECKLIST_ITEM4"),
      t("POINTS_CHECKLIST_ITEM5")
    ],
    [PREMIUM_PRODUCT]: [
      t("POINTS_CHECKLIST_ITEM1_WORKSHOPS"),
      t("POINTS_CHECKLIST_ITEM2_WORKSHOPS"),
      t("POINTS_CHECKLIST_ITEM3_WORKSHOPS"),
      t("POINTS_CHECKLIST_ITEM4_WORKSHOPS"),
      t("POINTS_CHECKLIST_ITEM5_WORKSHOPS"),
      t("POINTS_CHECKLIST_ITEM6_WORKSHOPS")
    ],
    [DIABETES_PROGRAM]: [
      t("DIABETES_CHECKLIST_ITEM1"),
      t("DIABETES_CHECKLIST_ITEM2"),
      t("DIABETES_CHECKLIST_ITEM3"),
      t("DIABETES_CHECKLIST_ITEM4"),
      t("DIABETES_CHECKLIST_ITEM5")
    ],
    [DIABETES_WORKSHOP_PROGRAM]: [
      t("DIABETES_CHECKLIST_ITEM1_WORKSHOPS"),
      t("DIABETES_CHECKLIST_ITEM2_WORKSHOPS"),
      t("DIABETES_CHECKLIST_ITEM3_WORKSHOPS"),
      t("DIABETES_CHECKLIST_ITEM4_WORKSHOPS"),
      t("DIABETES_CHECKLIST_ITEM5_WORKSHOPS"),
      t("DIABETES_CHECKLIST_ITEM6_WORKSHOPS")
    ],
    [GLP1_PROGRAM]: [
      t("GLP1_CHECKLIST_ITEM1"),
      t("GLP1_CHECKLIST_ITEM2"),
      t("GLP1_CHECKLIST_ITEM3"),
      t("GLP1_CHECKLIST_ITEM4"),
      t("GLP1_CHECKLIST_ITEM5")
    ],
    [GLP1_WORKSHOP_PROGRAM]: [
      t("GLP1_CHECKLIST_ITEM1_WORKSHOPS"),
      t("GLP1_CHECKLIST_ITEM2_WORKSHOPS"),
      t("GLP1_CHECKLIST_ITEM3_WORKSHOPS"),
      t("GLP1_CHECKLIST_ITEM4_WORKSHOPS"),
      t("GLP1_CHECKLIST_ITEM5_WORKSHOPS")
    ]
  }

  const pricingHeader = {
    [CORE_PRODUCT]: t("PRICING_HEADER"),
    [PREMIUM_PRODUCT]: t("PRICING_HEADER_WORKSHOPS"),
    [DIABETES_PROGRAM]: t("PRICING_HEADER"),
    [DIABETES_WORKSHOP_PROGRAM]: t("PRICING_HEADER_WORKSHOPS"),
    [GLP1_PROGRAM]: t("PRICING_HEADER"),
    [GLP1_WORKSHOP_PROGRAM]: t("PRICING_HEADER_WORKSHOPS")
  };

  const pricingDescription = {
    [CORE_PRODUCT]: t("PRICING_DESCRIPTION"),
    [PREMIUM_PRODUCT]: t("PRICING_DESCRIPTION"),
    [DIABETES_PROGRAM]: t("PRICING_DESCRIPTION_DIABETES"),
    [DIABETES_WORKSHOP_PROGRAM]: t("PRICING_DESCRIPTION_DIABETES"),
    [GLP1_PROGRAM]: t("PRICING_DESCRIPTION_GLP1"),
    [GLP1_WORKSHOP_PROGRAM]: t("PRICING_DESCRIPTION_GLP1")
  };

  const pricingDisclaimer = {
    [DIABETES_PROGRAM]: t("PRICING_DISCLAIMER_DIABETES"),
    [DIABETES_WORKSHOP_PROGRAM]: t("PRICING_DISCLAIMER_DIABETES")
  };

  return (
    <Loading loading={isLoading}>
      {!isError ? (
        <>
          <div className={styles.wrapper}>
            <SiteWrapper size="small">
              <ContentGutter>
                <h2 className={styles.h2}>
                  {pricingHeader[resultsScreen] || ""}
                </h2>
                <div className={styles.description}>
                  <AsHtml value={pricingDescription[resultsScreen] || ""} />
                </div>
              </ContentGutter>
              <Checklist items={checklistItems[resultsScreen] || []} />
              <ContentGutter>
                <div className={styles.disclaimer}>
                  {pricingDisclaimer[resultsScreen] || ""}
                </div>
              </ContentGutter>
              <ContentGutter>
                <h3 className={styles.h3}>{t("CHOOSE_DURATION")}</h3>
                {!isLoading && plans.length > 0 && (
                  <PricingCarousel
                    items={plans.map(plan => ({
                      children: (
                        <PricingCard
                          plan={plan}
                          selected={selectedPlan?.id === plan.id}
                          key={plan.id}
                        />
                      )
                    }))}
                    onSelect={onSelection}
                  />
                )}
                <a
                  className={styles.cta}
                  href={checkoutUrl}
                  data-testid="continue-pricing"
                  {...daProps(selectedPlan)}
                >
                  {t("CONTINUE_WITH").replace(
                    "{{planName}}",
                    selectedPlan?.contentMap
                      .get("OFFER_PLAN_NAME")
                      ?.toLowerCase()
                  )}
                </a>
              </ContentGutter>
            </SiteWrapper>
          </div>
          <div className={styles.whiteWrapper}>
            <SiteWrapper size="small">
              {!isLoading &&
                plans.map(plan => (
                  <OfferDisclaimer
                    key={plan.id}
                    title={plan.offerPlanName}
                    msg={plan.contentMap.get("DISCLAIMER")}
                  />
                ))}
            </SiteWrapper>
          </div>
        </>
      ) : (
        <Error onClick={retry as any} />
      )}
    </Loading>
  );
};
